// src/pages/FinancialsEditPage.js
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table,
} from "react-bootstrap";
import { BsPlusCircle, BsTrash, BsXLg } from "react-icons/bs";
import api from "../api";
import { useNavGuard } from "../hooks/useNavGuard";
import NavGuardModal from "../components/NavGuardModal";

const DEFAULT_INCOME_SOURCES = [
  "Employment Wage", "Social Security", "Disability", "Unemployment",
  "Family Assistance", "Child Support", "Pension",
];

const EXCLUDED_SOURCES = ["snap benefits", "wic"];

const DEFAULT_EXPENSES = [
  "Rent/Mortgage", "Food over Snap Benefits", "Electricity", "Gas", "Water",
  "Phone", "Car Payment", "Insurance", "Gasoline/Bus", "Medical",
  "Child Care", "Smoking", "Pets", "Child Support",
];

function toNumber(val) {
  const s = String(val ?? "").trim();
  if (!s) return 0;
  const n = Number(s.replace(/[$,]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function money(n) {
  return (Number.isFinite(n) ? n : 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

function genColKey() {
  return "c_" + crypto.randomUUID().slice(0, 8);
}

function blankExtra(cols) {
  const e = {};
  for (const c of cols) e[c.key] = "";
  return e;
}

function newIncomeRow(source = "", cols = []) {
  return { id: crypto.randomUUID(), source, client: "", spouse: "", extra: blankExtra(cols) };
}

function newExpenseRow(expense = "", saved = null) {
  return {
    id: crypto.randomUUID(),
    expense,
    amount: saved?.amount ?? "",
    provider: saved?.provider ?? "",
    account_no: saved?.account_no ?? "",
    notes: saved?.notes ?? "",
    date_due: saved?.date_due ?? "",
  };
}

function mergeExpenses(saved) {
  const byName = new Map((saved || []).map((r) => [(r.expense ?? "").trim().toLowerCase(), r]));
  const rows = DEFAULT_EXPENSES.map((name) => {
    const s = byName.get(name.toLowerCase());
    byName.delete(name.toLowerCase());
    return newExpenseRow(name, s ?? null);
  });
  for (const r of byName.values()) rows.push(newExpenseRow(r.expense ?? "", r));
  return rows;
}

export default function FinancialsEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");
  const [successMsg, setSuccess] = useState("");
  const [dirty, setDirty]       = useState(false);

  const [payDays, setPayDays]       = useState("");
  const [extraCols, setExtraCols]   = useState([]); // [{ key, label }]
  const [incomeRows, setIncomeRows] = useState(DEFAULT_INCOME_SOURCES.map((s) => newIncomeRow(s)));
  const [snapRow, setSnapRow]       = useState({ client: "", spouse: "", extra: {} });
  const [wicRow, setWicRow]         = useState({ client: "", spouse: "", extra: {} });
  const [expenseRows, setExpenseRows] = useState(DEFAULT_EXPENSES.map((e) => newExpenseRow(e)));
  const [version, setVersion] = useState(null);  // optimistic-lock token from the server

  const { showModal, resolveGuard } = useNavGuard(dirty);

  // SNAP Benefits and WIC are intentionally excluded from this total.
  // Every earner column (client, spouse, and any extra columns) is counted.
  const incomeTotals = useMemo(() => {
    const clientTotal = incomeRows.reduce((s, r) => s + toNumber(r.client), 0);
    const spouseTotal = incomeRows.reduce((s, r) => s + toNumber(r.spouse), 0);
    const extraTotals = {};
    let extraSum = 0;
    for (const c of extraCols) {
      const t = incomeRows.reduce((s, r) => s + toNumber(r.extra?.[c.key]), 0);
      extraTotals[c.key] = t;
      extraSum += t;
    }
    return { clientTotal, spouseTotal, extraTotals, combined: clientTotal + spouseTotal + extraSum };
  }, [incomeRows, extraCols]);

  const expenseTotal = useMemo(
    () => expenseRows.reduce((s, r) => s + toNumber(r.amount), 0),
    [expenseRows]
  );

  const netMonthly = incomeTotals.combined - expenseTotal;

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      try {
        const res = await api.get(`/clients/${id}/financials`);
        if (!alive) return;
        const d = res?.data || {};
        setPayDays(d.pay_days ?? "");

        const cols = Array.isArray(d.income_columns)
          ? d.income_columns.map((c) => ({ key: String(c.key), label: String(c.label ?? "") }))
          : [];
        setExtraCols(cols);
        const readExtra = (r) => {
          const e = {};
          for (const c of cols) e[c.key] = r?.[c.key] ?? "";
          return e;
        };

        if (Array.isArray(d.income) && d.income.length) {
          const snap = d.income.find((r) => (r.source ?? "").toLowerCase() === "snap benefits");
          const wic  = d.income.find((r) => (r.source ?? "").toLowerCase() === "wic");
          setSnapRow({ client: snap?.client ?? "", spouse: snap?.spouse ?? "", extra: readExtra(snap) });
          setWicRow({ client: wic?.client ?? "", spouse: wic?.spouse ?? "", extra: readExtra(wic) });
          setIncomeRows(
            d.income
              .filter((r) => !EXCLUDED_SOURCES.includes((r.source ?? "").toLowerCase()))
              .map((r) => ({ id: crypto.randomUUID(), source: r.source ?? "", client: r.client ?? "", spouse: r.spouse ?? "", extra: readExtra(r) }))
          );
        } else {
          setSnapRow({ client: "", spouse: "", extra: blankExtra(cols) });
          setWicRow({ client: "", spouse: "", extra: blankExtra(cols) });
          setIncomeRows(DEFAULT_INCOME_SOURCES.map((s) => newIncomeRow(s, cols)));
        }
        setExpenseRows(mergeExpenses(d.expenses));
        setVersion(d.financials_version ?? null);
        setDirty(false);
      } catch (_) {
        // fall back to defaults silently
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, [id]);

  function mark() { setDirty(true); setSuccess(""); }

  function updateIncome(rowId, key, value) { setIncomeRows((p) => p.map((r) => r.id === rowId ? { ...r, [key]: value } : r)); mark(); }
  function updateIncomeExtra(rowId, colKey, value) { setIncomeRows((p) => p.map((r) => r.id === rowId ? { ...r, extra: { ...r.extra, [colKey]: value } } : r)); mark(); }
  function addIncome()                     { setIncomeRows((p) => [...p, newIncomeRow("", extraCols)]); mark(); }
  function removeIncome(rowId)             { setIncomeRows((p) => p.filter((r) => r.id !== rowId)); mark(); }

  // ---- Extra earner columns ----
  function addColumn() {
    const key = genColKey();
    setExtraCols((p) => [...p, { key, label: "" }]);
    setIncomeRows((p) => p.map((r) => ({ ...r, extra: { ...r.extra, [key]: "" } })));
    setSnapRow((p) => ({ ...p, extra: { ...p.extra, [key]: "" } }));
    setWicRow((p) => ({ ...p, extra: { ...p.extra, [key]: "" } }));
    mark();
  }
  function removeColumn(key) {
    const strip = (e) => { const { [key]: _drop, ...rest } = e || {}; return rest; };
    setExtraCols((p) => p.filter((c) => c.key !== key));
    setIncomeRows((p) => p.map((r) => ({ ...r, extra: strip(r.extra) })));
    setSnapRow((p) => ({ ...p, extra: strip(p.extra) }));
    setWicRow((p) => ({ ...p, extra: strip(p.extra) }));
    mark();
  }
  function updateColLabel(key, label) { setExtraCols((p) => p.map((c) => c.key === key ? { ...c, label } : c)); mark(); }

  function updateExpense(rowId, key, value) { setExpenseRows((p) => p.map((r) => r.id === rowId ? { ...r, [key]: value } : r)); mark(); }
  function addExpense()                     { setExpenseRows((p) => [...p, newExpenseRow("")]); mark(); }
  function removeExpense(rowId)             { setExpenseRows((p) => p.filter((r) => r.id !== rowId)); mark(); }

  async function doSave() {
    setSaving(true); setError(""); setSuccess("");
    try {
      const res = await api.put(`/clients/${id}/financials`, {
        financials_version: version,
        pay_days: payDays,
        income_columns: extraCols,
        income: [
          ...incomeRows.map(({ source, client, spouse, extra }) => ({ source, client, spouse, ...(extra || {}) })),
          { source: "SNAP Benefits", client: snapRow.client, spouse: snapRow.spouse, ...(snapRow.extra || {}) },
          { source: "WIC",           client: wicRow.client,  spouse: wicRow.spouse,  ...(wicRow.extra || {}) },
        ],
        expenses: expenseRows.map(({ expense, amount, provider, account_no, notes, date_due }) => ({
          expense, amount, provider, account_no, notes, date_due,
        })),
      });
      setVersion(res?.data?.financials_version ?? version);
      setDirty(false);
      setSuccess("Financials saved.");
      return true;
    } catch (e) {
      if (e?.response?.status === 409) {
        setError("This client's financials were changed by someone else since you opened this page. Please reload and re-enter your changes.");
      } else {
        setError(e?.response?.data?.error || e?.message || "Failed to save financials.");
      }
      return false;
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="p-3"><Spinner animation="border" size="sm" className="me-2" />Loading financials...</div>;

  return (
    <div className="p-3">
      <NavGuardModal
        show={showModal}
        saving={saving}
        error={error}
        onSave={async () => { const ok = await doSave(); if (ok) resolveGuard(true); }}
        onDiscard={() => { setDirty(false); resolveGuard(true); }}
        onCancel={() => resolveGuard(false)}
      />

      <Form onSubmit={async (e) => { e.preventDefault(); const ok = await doSave(); if (ok) navigate(`/clients/${id}/view/financials`); }}>
        <Row className="mb-3">
          <Col>
            <h3 className="mb-0">
              Edit Financials
              {dirty && <span className="text-warning ms-2" style={{ fontSize: "0.9rem" }}>● Unsaved</span>}
            </h3>
            <div className="text-muted">Client ID: {id}</div>
            <div className="text-muted mt-1" style={{ fontSize: "0.95rem" }}>
              <span className="me-2"><b>Income:</b> ${money(incomeTotals.combined)} <span style={{ fontSize: "0.85em" }}>(excl. SNAP/WIC)</span></span>
              <span className="me-2"><b>Expenses:</b> ${money(expenseTotal)}</span>
              <span><b>Net:</b> <Badge bg={netMonthly >= 0 ? "success" : "danger"}>${money(netMonthly)}</Badge></span>
            </div>
          </Col>
          <Col className="d-flex justify-content-end align-items-start gap-2">
            <Button variant="secondary" type="button" onClick={() => navigate(-1)} disabled={saving}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? <><Spinner animation="border" size="sm" className="me-2" />Saving...</> : "Save"}
            </Button>
          </Col>
        </Row>

        {error && !showModal ? <Alert variant="danger" className="mb-3">{error}</Alert> : null}
        {successMsg ? <Alert variant="success" className="mb-3">{successMsg}</Alert> : null}

        <Card className="mb-3">
          <Card.Header>Pay days</Card.Header>
          <Card.Body>
            <Row><Col md={6} lg={4}>
              <Form.Group>
                <Form.Label>Pay days</Form.Label>
                <Form.Control type="text" value={payDays} onChange={(e) => { setPayDays(e.target.value); mark(); }} placeholder="e.g., Friday / 1st & 15th / Weekly" />
              </Form.Group>
            </Col></Row>
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <span>Income</span>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" type="button" onClick={addColumn}><BsPlusCircle className="me-2" />Add Column</Button>
              <Button variant="outline-primary" type="button" onClick={addIncome}><BsPlusCircle className="me-2" />Add Row</Button>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0">
              <thead><tr>
                <th style={{ minWidth: 180 }}>Income Source</th>
                <th style={{ minWidth: 110 }}>Client</th>
                <th style={{ minWidth: 110 }}>Spouse</th>
                {extraCols.map((c) => (
                  <th key={c.key} style={{ minWidth: 140 }}>
                    <div className="d-flex align-items-center gap-1">
                      <Form.Control
                        size="sm"
                        type="text"
                        value={c.label}
                        onChange={(e) => updateColLabel(c.key, e.target.value)}
                        placeholder="Name / relationship"
                      />
                      <Button variant="link" size="sm" className="text-danger p-0 px-1" title="Remove column" onClick={() => removeColumn(c.key)}><BsXLg /></Button>
                    </div>
                  </th>
                ))}
                <th style={{ minWidth: 90 }} className="text-end">Actions</th>
              </tr></thead>
              <tbody>
                {incomeRows.map((r) => (
                  <tr key={r.id}>
                    <td><Form.Control type="text" value={r.source} onChange={(e) => updateIncome(r.id, "source", e.target.value)} placeholder="Income source" /></td>
                    <td><Form.Control type="text" inputMode="decimal" value={r.client} onChange={(e) => updateIncome(r.id, "client", e.target.value)} placeholder="0.00" /></td>
                    <td><Form.Control type="text" inputMode="decimal" value={r.spouse} onChange={(e) => updateIncome(r.id, "spouse", e.target.value)} placeholder="0.00" /></td>
                    {extraCols.map((c) => (
                      <td key={c.key}><Form.Control type="text" inputMode="decimal" value={r.extra?.[c.key] ?? ""} onChange={(e) => updateIncomeExtra(r.id, c.key, e.target.value)} placeholder="0.00" /></td>
                    ))}
                    <td className="text-end"><Button variant="outline-danger" type="button" onClick={() => removeIncome(r.id)}><BsTrash /></Button></td>
                  </tr>
                ))}
                <tr className="table-light">
                  <td className="fst-italic text-muted">SNAP Benefits</td>
                  <td><Form.Control type="text" inputMode="decimal" value={snapRow.client} onChange={(e) => { setSnapRow((p) => ({ ...p, client: e.target.value })); mark(); }} placeholder="0.00" /></td>
                  <td><Form.Control type="text" inputMode="decimal" value={snapRow.spouse} onChange={(e) => { setSnapRow((p) => ({ ...p, spouse: e.target.value })); mark(); }} placeholder="0.00" /></td>
                  {extraCols.map((c) => (
                    <td key={c.key}><Form.Control type="text" inputMode="decimal" value={snapRow.extra?.[c.key] ?? ""} onChange={(e) => { const v = e.target.value; setSnapRow((p) => ({ ...p, extra: { ...p.extra, [c.key]: v } })); mark(); }} placeholder="0.00" /></td>
                  ))}
                  <td></td>
                </tr>
                <tr className="table-light">
                  <td className="fst-italic text-muted">WIC</td>
                  <td><Form.Control type="text" inputMode="decimal" value={wicRow.client} onChange={(e) => { setWicRow((p) => ({ ...p, client: e.target.value })); mark(); }} placeholder="0.00" /></td>
                  <td><Form.Control type="text" inputMode="decimal" value={wicRow.spouse} onChange={(e) => { setWicRow((p) => ({ ...p, spouse: e.target.value })); mark(); }} placeholder="0.00" /></td>
                  {extraCols.map((c) => (
                    <td key={c.key}><Form.Control type="text" inputMode="decimal" value={wicRow.extra?.[c.key] ?? ""} onChange={(e) => { const v = e.target.value; setWicRow((p) => ({ ...p, extra: { ...p.extra, [c.key]: v } })); mark(); }} placeholder="0.00" /></td>
                  ))}
                  <td></td>
                </tr>
                <tr>
                  <td className="fw-bold">Total Income <span className="fw-normal text-muted" style={{ fontSize: "0.8em" }}>(excl. SNAP/WIC)</span></td>
                  <td><Form.Control type="text" value={money(incomeTotals.clientTotal)} readOnly /></td>
                  <td><Form.Control type="text" value={money(incomeTotals.spouseTotal)} readOnly /></td>
                  {extraCols.map((c) => (
                    <td key={c.key}><Form.Control type="text" value={money(incomeTotals.extraTotals[c.key] || 0)} readOnly /></td>
                  ))}
                  <td><Form.Control type="text" value={money(incomeTotals.combined)} readOnly title="Combined (all earners, excl. SNAP/WIC)" /></td>
                </tr>
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <span>Expenses</span>
            <Button variant="outline-primary" type="button" onClick={addExpense}><BsPlusCircle className="me-2" />Add Row</Button>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0">
              <thead><tr>
                <th style={{ minWidth: 220 }}>Expenses</th>
                <th style={{ width: 140 }}>Amount</th>
                <th style={{ minWidth: 200 }}>Provider</th>
                <th style={{ width: 140 }}>Account#</th>
                <th style={{ minWidth: 240 }}>Notes</th>
                <th style={{ width: 160 }}>Date Due</th>
                <th style={{ width: 110 }} className="text-end">Actions</th>
              </tr></thead>
              <tbody>
                {expenseRows.map((r) => (
                  <tr key={r.id}>
                    <td><Form.Control type="text" value={r.expense} onChange={(e) => updateExpense(r.id, "expense", e.target.value)} placeholder="Expense" /></td>
                    <td><Form.Control type="text" inputMode="decimal" value={r.amount} onChange={(e) => updateExpense(r.id, "amount", e.target.value)} placeholder="0.00" /></td>
                    <td><Form.Control type="text" value={r.provider} onChange={(e) => updateExpense(r.id, "provider", e.target.value)} placeholder="Provider" /></td>
                    <td><Form.Control type="text" value={r.account_no} onChange={(e) => updateExpense(r.id, "account_no", e.target.value)} placeholder="Account #" /></td>
                    <td><Form.Control type="text" value={r.notes} onChange={(e) => updateExpense(r.id, "notes", e.target.value)} placeholder="Notes" /></td>
                    <td><Form.Control type="date" value={r.date_due || ""} onChange={(e) => updateExpense(r.id, "date_due", e.target.value)} /></td>
                    <td className="text-end"><Button variant="outline-danger" type="button" onClick={() => removeExpense(r.id)}><BsTrash /></Button></td>
                  </tr>
                ))}
                <tr>
                  <td className="fw-bold">Total Expenses</td>
                  <td><Form.Control type="text" value={money(expenseTotal)} readOnly /></td>
                  <td colSpan={5}></td>
                </tr>
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </Form>
    </div>
  );
}
